import type { ReactNode } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Toggle } from "@ui/toggle";
import type { GridHeaderItem, GridHeaderItemPatch } from "./types";

type GridHeaderVisibilityFieldsProps<T extends GridHeaderItem> = {
  header: T;
  updateHeader: (patch: GridHeaderItemPatch<T>) => void;
  children?: ReactNode;
};

export function GridHeaderVisibilityFields<T extends GridHeaderItem>({
  header,
  updateHeader,
  children,
}: GridHeaderVisibilityFieldsProps<T>) {
  const isHidden = (header.hide as boolean) ?? false;
  const isRequired = (header.required as boolean) ?? false;
  const isReadonly = (header.readonly as boolean) ?? false;
  const isDisabled = (header.disabled as boolean) ?? false;
  const isEditable = !isReadonly;
  const disableDisabled = isRequired || isEditable;

  return (
    <>
      {children}
      <div className="flex flex-row justify-between items-center">
        <span className="text-base font-medium text-dark">Show component</span>
        <Toggle
          pressed={!isHidden}
          onPressedChange={(pressed) =>
            updateHeader({ hide: !pressed } as GridHeaderItemPatch<T>)
          }
        />
      </div>

      {!isHidden && (
        <>
          <div className="flex flex-row justify-between items-center">
            <span className="text-base font-medium text-dark">Required</span>
            <Checkbox
              checked={isRequired}
              disabled={isDisabled}
              onCheckedChange={(checked) =>
                updateHeader({ required: checked } as GridHeaderItemPatch<T>)
              }
            />
          </div>

          <div className="flex flex-row justify-between items-center">
            <span className="text-base font-medium text-dark">Editable</span>
            <Checkbox
              checked={isEditable}
              disabled={isDisabled}
              onCheckedChange={(checked) =>
                updateHeader({ readonly: !checked } as GridHeaderItemPatch<T>)
              }
            />
          </div>

          <div className="flex flex-row justify-between items-center">
            <span className="text-base font-medium text-dark">Disabled</span>
            <Checkbox
              checked={isDisabled}
              disabled={disableDisabled}
              onCheckedChange={(checked) =>
                updateHeader({ disabled: checked } as GridHeaderItemPatch<T>)
              }
            />
          </div>
        </>
      )}
    </>
  );
}
